import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { qrMatrix, qrPath } from '../qr';
import { shareLink } from '../native';
import { toast } from './feedback';

// "Share MedXMeet" (brief 035, P-9): the share sheet or a copied link, plus a QR code
// drawn on the device for sharing in person. openShareSheet() opens it from anywhere;
// <ShareSheetHost/> is mounted once with the toasts.

export const SHARE_URL = 'https://medxmeet.com/?ref=share';
export const SHARE_TEXT = "I'm using MedXMeet to find USMLE study partners: free, any time zone, matched by subjects and schedule. medxmeet.com";

let listener = null;
export const openShareSheet = (opts = {}) => listener && listener({
  title: 'Share MedXMeet', lead: 'Send MedXMeet to a classmate or a group chat, or let someone scan the code.',
  url: SHARE_URL, text: SHARE_TEXT, button: 'Share MedXMeet', ...opts,
});

// Invite a study partner (P-1): the same sheet with the user's own invite link.
export async function openInviteSheet(uid, profile) {
  try {
    // Loaded on use, so the toast host (which mounts this sheet) doesn't pull in Firestore.
    const { myInviteUrl, INVITE_TEXT } = await import('../invites');
    const url = await myInviteUrl(uid, profile);
    openShareSheet({
      title: 'Invite a study partner',
      lead: "Bring a classmate or friend. You'll be connected as soon as they join.",
      url, text: INVITE_TEXT(url), button: 'Share invite',
    });
  } catch (e) {
    console.error(e);
    toast('Could not create your invite link. Check your connection and try again.', { type: 'error' });
  }
}

export function ShareSheetHost() {
  const [sheet, setSheet] = useState(null);
  useEffect(() => { listener = setSheet; return () => { if (listener === setSheet) listener = null; }; }, []);
  const close = useCallback(() => setSheet(null), []);
  return sheet ? <ShareSheet {...sheet} onClose={close} /> : null;
}

function ShareSheet({ title, lead, url, text, button, onClose }) {
  const qr = useMemo(() => qrPath(qrMatrix(url)), [url]);
  const share = async () => { if (await shareLink({ title: 'MedXMeet', text, url }) === 'shared') onClose(); };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal share-sheet" role="dialog" aria-label={title} onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <p className="muted share-lead">{lead}</p>
        <svg className="share-qr" viewBox={`0 0 ${qr.size} ${qr.size}`} role="img" aria-label={`QR code for ${url}`} shapeRendering="crispEdges">
          <rect width={qr.size} height={qr.size} fill="#fff" />
          <path d={qr.d} fill="#121417" />
        </svg>
        <p className="small muted share-url">{url.replace('https://', '')}</p>
        <button className="btn full" onClick={share}>{button}</button>
      </div>
    </div>
  );
}
